// src/components/warranties/WarrantyCreateForm.jsx
import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { FormControlLabel, Switch } from "@mui/material";

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";

import { djangoApi } from "../../utils/axios/axios.utils";
import WarrantyPhotoUploader from "./WarrantyPhotoUploader";
import WarrantyPhotosGallery from "./WarrantyPhotosGallery";

/**
 * ✅ Create + Edit (upsert)
 * - Se `warranty` vier (com id/pk), entra em modo edição e faz PATCH.
 * - Se `warranty` for null, cria (POST).
 *
 * ✅ number_ref NÃO é gerado no front.
 * - No create: não enviamos number_ref (backend define).
 * - No edit: mostramos number_ref read-only (se existir).
 *
 * ✅ Vínculo forte: sempre relacionar pela FK do KitProduct (kit_product_pk).
 *
 * ✅ Mês de venda:
 * - No input: mostrar "jan/2026" derivado de selectedProduct.kit__pay_date
 * - No payload: enviar string "jan/2026" em sale_month (model é CharField)
 *
 * ✅ Solução:
 * - UI chama "Solução", mas salva no campo `observacao` (herdado de Base).
 */

function normalizePhotos(rawOrList) {
    // se já veio array do backend
    if (Array.isArray(rawOrList)) {
        return rawOrList
            .map((x) => {
                if (!x) return null;
                if (typeof x === "string") return x.trim();
                if (typeof x === "object") return String(x.url || x.downloadURL || x.href || "").trim();
                return null;
            })
            .filter(Boolean);
    }

    // string "1 por linha"
    return String(rawOrList || "")
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s && s.startsWith("http"));
}


function photosToRaw(list) {
    if (!Array.isArray(list) || !list.length) return "";
    return list.filter(Boolean).join("\n");
}

function getTodayISO() {
    return new Date().toISOString().slice(0, 10);
}

function getSaleMonthLabel(kitPayDate) {
    // "YYYY-MM-DD" -> "jan/2026"
    const s = String(kitPayDate || "").trim();
    if (!s) return "-";

    const parts = s.split("-");
    if (parts.length < 2) return "-";

    const y = parts[0];
    const m = Number(parts[1]);
    if (!Number.isFinite(m) || m < 1 || m > 12) return "-";

    const months = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
    return `${months[m - 1]}/${y}`;
}

function buildPayload({ seller, product, form }) {
    const photos_urls = normalizePhotos(form.photos_urls_raw);

    const sale_month = getSaleMonthLabel(product?.kit__pay_date);
    const sale_month_value = sale_month && sale_month !== "-" ? sale_month : null;

    const payload = {
        kit_product_pk: product?.pk ?? null,
        seller_id: seller?.id ?? null,

        received_at: form.received_at || null,
        number_manual: !!form.number_manual,
        number_ref: form.number_manual
            ? (String(form.number_ref_manual || "").trim() || null)
            : null,
        product_code: String(form.product_code || "").trim() || null,

        sale_month: sale_month_value,
        product_description: String(form.product_description || "").trim() || null,
        problem_reported: String(form.problem_reported || "").trim() || null,

        is_warranty: !!form.is_warranty,
        sent_to_factory: !!form.sent_to_factory,
        internal_repair: !!form.internal_repair,

        lot_code: String(form.lot_code || "").trim() || null,

        // ✅ choices do seu model (Status)
        status: String(form.status || "OPEN").trim() || "OPEN",

        customer_contact: String(form.customer_contact || "").trim() || null,
        notes: String(form.notes || "").trim() || null,

        // ✅ Base.observacao (UI = "Solução")
        observacao: String(form.observacao || "").trim() || null,

        photos_urls,
    };

    payload.product_snapshot = product
        ? {
            kit_product_pk: product.pk,
            product_id_produto: product.product_id_produto,
            product_id_produto_pk: product.product_id_produto_pk,
            content_type__model: product.content_type__model,
            product_id_produto_color: product.product_id_produto_color,
            product_tamanho: product.product_tamanho,
            sell_price: product.sell_price,
            quantity: product.quantity,
            quantity_sold: product.quantity_sold,
            kit__kit_number: product.kit__kit_number,
            kit__pay_date: product.kit__pay_date,
            kit__seller__nome: product.kit__seller__nome,
            imageUrl: product.imageUrl,
        }
        : null;

    return payload;
}

export default function WarrantyCreateForm({
    seller,
    selectedProduct,
    warranty,
    theme,
    colors,
    subtleBorder,
    softText,
    fmtBRL,
    onSuccess,
    onCancelEdit,
}) {
    const isEdit = !!(warranty && (warranty.id || warranty.pk));
    const warrantyId = isEdit ? (warranty.id || warranty.pk) : null;

    const [form, setForm] = useState({
        number_ref: "",

        number_manual: false,
        number_ref_manual: "",

        received_at: "",
        product_code: "",
        product_description: "",
        problem_reported: "",

        is_warranty: true,
        sent_to_factory: false,
        internal_repair: false,

        lot_code: "",
        status: "OPEN",

        customer_contact: "",
        notes: "",

        // ✅ NEW: Solução -> Base.observacao
        observacao: "",

        photos_urls_raw: "",
    });

    const [submitState, setSubmitState] = useState({
        status: "idle", // idle | sending | success | error
        message: "",
    });

    const saleMonthLabel = useMemo(
        () => getSaleMonthLabel(selectedProduct?.kit__pay_date),
        [selectedProduct?.kit__pay_date]
    );

    useEffect(() => {
        setSubmitState({ status: "idle", message: "" });

        if (isEdit) {
            setForm({
                number_ref: String(warranty.number_ref || warranty.number || "").trim() || "",

                received_at: (warranty.received_at || "").slice(0, 10) || "",
                product_code: String(warranty.product_code || warranty.codigo || "").trim() || "-",
                product_description: String(warranty.product_description || warranty.descricao_produto || "").trim() || "",
                problem_reported: String(warranty.problem_reported || warranty.problema_apresentado || "").trim() || "",

                is_warranty: warranty.is_warranty ?? warranty.e_garantia ?? true,
                sent_to_factory: warranty.sent_to_factory ?? warranty.enviado_fabrica ?? false,
                internal_repair: warranty.internal_repair ?? warranty.conserto_interno ?? false,

                lot_code: String(warranty.lot_code || warranty.lote || "").trim() || "",
                status: String(warranty.status || "").trim() || "OPEN",

                customer_contact: String(warranty.customer_contact || "").trim() || "",
                notes: String(warranty.notes || "").trim() || "",

                // ✅ hydrate solução
                observacao: String(warranty.observacao || "").trim() || "",

                photos_urls_raw: photosToRaw(warranty.photos_urls || warranty.fotos_urls || []),
            });

            return;
        }

        if (!selectedProduct?.pk) {
            setForm((p) => ({ ...p, number_ref: "" }));
            return;
        }

        setForm((prev) => ({
            ...prev,
            number_ref: "",

            received_at: prev.received_at || getTodayISO(),
            product_code: prev.product_code || String(selectedProduct.product_id_produto || "").slice(0, 6) || "-",
            product_description:
                prev.product_description ||
                `${String(selectedProduct.content_type__model || "").toUpperCase()} • Cor: ${selectedProduct.product_id_produto_color || "-"}${selectedProduct.product_tamanho !== null && selectedProduct.product_tamanho !== undefined
                    ? ` • Tam: ${selectedProduct.product_tamanho}`
                    : ""
                } • Kit #${selectedProduct.kit__kit_number ?? "-"}`,

            notes:
                prev.notes ||
                `Item KitProduct #${selectedProduct.pk} • Kit #${selectedProduct.kit__kit_number ?? "-"} • Pago: ${selectedProduct.kit__pay_date || "-"
                }`,

            status: prev.status || "OPEN",
        }));
    }, [isEdit, warranty, selectedProduct?.pk]);

    const payloadPreview = useMemo(() => {
        return buildPayload({ seller, product: selectedProduct, form });
    }, [seller, selectedProduct, form]);

    useEffect(() => {
        const warrantyNumber = String(
            warranty?.number_ref ??
            warranty?.number_manual ??
            ""
        );

        const shouldLog =
            !isEdit ||
            ["747", "746", "745"].includes(warrantyNumber);

        if (!shouldLog) return;

        const snapshot =
            warranty?.product_snapshot ??
            warranty?.kit_product_snapshot ??
            null;

        console.groupCollapsed(
            `%c[WARRANTY DEBUG] ${isEdit ? "EDIÇÃO" : "CRIAÇÃO"} ${warrantyNumber ? `#${warrantyNumber}` : ""
            }`,
            "color:#ff9800;font-weight:bold;font-size:13px;"
        );

        console.log("1. Garantia recebida pelo formulário:", warranty);

        console.table({
            warranty_id:
                warranty?.id ??
                warranty?.pk ??
                null,

            number_ref:
                warranty?.number_ref ??
                null,

            warranty_seller:
                warranty?.seller ??
                null,

            warranty_seller_id:
                warranty?.seller_id ??
                warranty?.seller?.id ??
                null,

            warranty_seller_nome:
                warranty?.seller_nome ??
                warranty?.seller_name ??
                warranty?.seller?.nome ??
                null,

            warranty_kit_product:
                warranty?.kit_product ??
                null,

            warranty_kit_product_id:
                warranty?.kit_product_id ??
                warranty?.kit_product_pk ??
                warranty?.kit_product?.id ??
                warranty?.kit_product?.pk ??
                null,

            warranty_product_code:
                warranty?.product_code ??
                null,

            snapshot_kit_product_pk:
                snapshot?.kit_product_pk ??
                snapshot?.pk ??
                null,

            snapshot_seller_id:
                snapshot?.seller_id ??
                snapshot?.kit__seller_id ??
                null,

            snapshot_seller_nome:
                snapshot?.seller_nome ??
                snapshot?.kit__seller__nome ??
                null,

            snapshot_product_code:
                snapshot?.product_id_produto ??
                null,

            snapshot_model:
                snapshot?.content_type__model ??
                null,

            snapshot_kit_number:
                snapshot?.kit__kit_number ??
                null,
        });

        console.log("2. Seller efetivo recebido por props:", seller);

        console.table({
            prop_seller_id:
                seller?.id ??
                null,

            prop_seller_nome:
                seller?.nome ??
                null,
        });

        console.log(
            "3. selectedProduct efetivo recebido por props:",
            selectedProduct
        );

        console.table({
            selected_product_pk:
                selectedProduct?.pk ??
                null,

            selected_kit_product_pk:
                selectedProduct?.kit_product_pk ??
                null,

            selected_product_code:
                selectedProduct?.product_id_produto ??
                null,

            selected_product_original_pk:
                selectedProduct?.product_id_produto_pk ??
                selectedProduct?.object_id ??
                null,

            selected_seller_id:
                selectedProduct?.seller_id ??
                selectedProduct?.kit__seller_id ??
                null,

            selected_seller_nome:
                selectedProduct?.seller_nome ??
                selectedProduct?.kit__seller__nome ??
                null,

            selected_model:
                selectedProduct?.content_type__model ??
                null,

            selected_kit_number:
                selectedProduct?.kit__kit_number ??
                null,

            selected_pay_date:
                selectedProduct?.kit__pay_date ??
                null,
        });

        console.log("4. Payload que será enviado:", payloadPreview);

        console.table({
            payload_kit_product_pk:
                payloadPreview?.kit_product_pk ??
                null,

            payload_seller_id:
                payloadPreview?.seller_id ??
                null,

            payload_number_ref:
                payloadPreview?.number_ref ??
                null,

            payload_product_code:
                payloadPreview?.product_code ??
                null,

            payload_snapshot_kit_product_pk:
                payloadPreview?.product_snapshot?.kit_product_pk ??
                null,

            payload_snapshot_product_code:
                payloadPreview?.product_snapshot?.product_id_produto ??
                null,

            payload_snapshot_seller_id:
                payloadPreview?.product_snapshot?.seller_id ??
                payloadPreview?.product_snapshot?.kit__seller_id ??
                null,

            payload_snapshot_seller_nome:
                payloadPreview?.product_snapshot?.seller_nome ??
                payloadPreview?.product_snapshot?.kit__seller__nome ??
                null,
        });

        const warnings = [];

        const warrantyKitProductId =
            warranty?.kit_product_id ??
            warranty?.kit_product_pk ??
            warranty?.kit_product?.id ??
            warranty?.kit_product?.pk ??
            warranty?.kit_product ??
            null;

        const selectedKitProductId =
            selectedProduct?.pk ??
            selectedProduct?.kit_product_pk ??
            null;

        const snapshotKitProductId =
            snapshot?.kit_product_pk ??
            snapshot?.pk ??
            null;

        const warrantySellerId =
            warranty?.seller_id ??
            warranty?.seller?.id ??
            (
                typeof warranty?.seller === "number"
                    ? warranty.seller
                    : null
            );

        const selectedSellerId =
            seller?.id ??
            selectedProduct?.seller_id ??
            selectedProduct?.kit__seller_id ??
            null;

        const snapshotSellerId =
            snapshot?.seller_id ??
            snapshot?.kit__seller_id ??
            null;

        if (
            warrantyKitProductId != null &&
            selectedKitProductId != null &&
            String(warrantyKitProductId) !==
            String(selectedKitProductId)
        ) {
            warnings.push(
                `KitProduct divergente: garantia=${warrantyKitProductId}, selecionado=${selectedKitProductId}`
            );
        }

        if (
            warrantyKitProductId != null &&
            snapshotKitProductId != null &&
            String(warrantyKitProductId) !==
            String(snapshotKitProductId)
        ) {
            warnings.push(
                `KitProduct divergente: garantia=${warrantyKitProductId}, snapshot=${snapshotKitProductId}`
            );
        }

        if (
            warrantySellerId != null &&
            selectedSellerId != null &&
            String(warrantySellerId) !==
            String(selectedSellerId)
        ) {
            warnings.push(
                `Seller divergente: garantia=${warrantySellerId}, props=${selectedSellerId}`
            );
        }

        if (
            warrantySellerId != null &&
            snapshotSellerId != null &&
            String(warrantySellerId) !==
            String(snapshotSellerId)
        ) {
            warnings.push(
                `Seller divergente: garantia=${warrantySellerId}, snapshot=${snapshotSellerId}`
            );
        }

        if (
            selectedKitProductId != null &&
            payloadPreview?.kit_product_pk != null &&
            String(selectedKitProductId) !==
            String(payloadPreview.kit_product_pk)
        ) {
            warnings.push(
                `Payload com KitProduct incorreto: selecionado=${selectedKitProductId}, payload=${payloadPreview.kit_product_pk}`
            );
        }

        if (warnings.length) {
            console.error(
                "⚠️ DIVERGÊNCIAS ENCONTRADAS:",
                warnings
            );
        } else {
            console.log(
                "%c✅ Nenhuma divergência detectada neste render.",
                "color:#4caf50;font-weight:bold;"
            );
        }

        console.groupEnd();
    }, [
        isEdit,
        warranty,
        seller,
        selectedProduct,
        payloadPreview,
    ]);

    const canSubmit =
        !!seller?.id &&
        !!selectedProduct?.pk &&
        submitState.status !== "sending" &&
        String(form.received_at || "").trim().length > 0 &&
        String(form.problem_reported || "").trim().length > 0 &&
        String(form.status || "").trim().length > 0 &&
        (!form.number_manual || String(form.number_ref_manual || "").trim().length > 0);



    const handleSubmit = async () => {
        if (!canSubmit) return;

        try {
            setSubmitState({ status: "sending", message: "" });

            let res;

            if (isEdit) {
                const id = warranty.id || warranty.pk;
                res = await djangoApi.patch(`products/warranties/${id}/`, payloadPreview, {
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                });
            } else {
                res = await djangoApi.post("products/warranties/", payloadPreview, {
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                });
            }

            setSubmitState({
                status: "success",
                message: res?.data?.msg || (isEdit ? "Garantia atualizada com sucesso." : "Garantia criada com sucesso."),
            });

            if (typeof onSuccess === "function") onSuccess(res?.data);
        } catch (err) {
            const data = err?.response?.data;
            const detail =
                data?.detail ||
                data?.msg ||
                (data && typeof data === "object"
                    ? Object.entries(data)
                        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`)
                        .join(" | ")
                    : null) ||
                "Falha ao salvar garantia.";

            setSubmitState({ status: "error", message: detail });
        }
    };

    const handleReset = () => {
        if (isEdit) {
            setSubmitState({ status: "idle", message: "" });
            setForm({
                number_ref: String(warranty.number_ref || warranty.number || "").trim() || "",

                received_at: (warranty.received_at || "").slice(0, 10) || "",
                product_code: String(warranty.product_code || warranty.codigo || "").trim() || "-",
                product_description: String(warranty.product_description || warranty.descricao_produto || "").trim() || "",
                problem_reported: String(warranty.problem_reported || warranty.problema_apresentado || "").trim() || "",

                is_warranty: warranty.is_warranty ?? warranty.e_garantia ?? true,
                sent_to_factory: warranty.sent_to_factory ?? warranty.enviado_fabrica ?? false,
                internal_repair: warranty.internal_repair ?? warranty.conserto_interno ?? false,

                lot_code: String(warranty.lot_code || warranty.lote || "").trim() || "",
                status: String(warranty.status || "").trim() || "OPEN",

                customer_contact: String(warranty.customer_contact || "").trim() || "",
                notes: String(warranty.notes || "").trim() || "",
                observacao: String(warranty.observacao || "").trim() || "",
                photos_urls_raw: photosToRaw(warranty.photos_urls || warranty.fotos_urls || []),
            });
            return;
        }

        setForm({
            number_ref: "",
            received_at: selectedProduct?.pk ? getTodayISO() : "",
            product_code: selectedProduct?.pk ? String(selectedProduct.product_id_produto || "").slice(0, 6) || "-" : "",
            product_description: "",
            problem_reported: "",
            is_warranty: true,
            sent_to_factory: false,
            internal_repair: false,
            lot_code: "",
            status: "OPEN",
            customer_contact: "",
            notes: "",
            observacao: "",
            photos_urls_raw: "",
        });
        setSubmitState({ status: "idle", message: "" });
    };

    if (!seller) return null;

    return (
        <>
            <Divider sx={{ my: 3 }} />

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 2, flexWrap: "wrap" }}>
                <Typography variant="subtitle1" fontWeight={900}>
                    3) {isEdit ? "Editar garantia" : "Abrir garantia"}
                </Typography>

                {isEdit && typeof onCancelEdit === "function" && (
                    <Button size="small" variant="outlined" color="warning" onClick={onCancelEdit}>
                        Sair da edição
                    </Button>
                )}
            </Stack>

            {!selectedProduct ? (
                <Typography color={softText} sx={{ mt: 1 }}>
                    Selecione um produto para {isEdit ? "editar" : "abrir"} a garantia.
                </Typography>
            ) : (
                <Paper
                    variant="outlined"
                    sx={{
                        mt: 2,
                        p: 3,
                        borderRadius: 2,
                        border: subtleBorder,
                        bgcolor: theme?.palette?.mode === "dark" ? colors?.pink?.[700] : "#fafafa",

                        "& .MuiTextField-root .MuiInputBase-root": {
                            borderRadius: 2,
                            backgroundColor: theme?.palette?.mode === "dark"
                                ? "rgba(255,255,255,0.06)"
                                : "rgba(2,6,23,0.04)",
                            transition: "all 0.15s ease",
                        },
                        "& .MuiTextField-root .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme?.palette?.mode === "dark"
                                ? "rgba(255,255,255,0.18)"
                                : "rgba(2,6,23,0.18)",
                        },
                        "& .MuiTextField-root:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme?.palette?.mode === "dark"
                                ? "rgba(255,255,255,0.28)"
                                : "rgba(2,6,23,0.28)",
                        },
                        "& .MuiTextField-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderWidth: 2,
                        },
                    }}

                >
                    <Stack spacing={2}>
                        <Box>
                            <Typography fontWeight={900}>
                                Produto: #{selectedProduct.product_id_produto} • KitProduct PK: {selectedProduct.pk}
                            </Typography>

                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                <Chip variant="outlined" label={`Kit: #${selectedProduct.kit__kit_number ?? "-"}`} />
                                <Chip variant="outlined" label={`Pgto kit: ${selectedProduct.kit__pay_date || "-"}`} />
                                <Chip variant="outlined" label={`Mês venda: ${saleMonthLabel}`} />
                                {!!selectedProduct.product_id_produto_color && (
                                    <Chip variant="outlined" label={`Cor: ${selectedProduct.product_id_produto_color}`} />
                                )}
                                {selectedProduct.product_tamanho !== null && selectedProduct.product_tamanho !== undefined && (
                                    <Chip variant="outlined" label={`Tamanho: ${selectedProduct.product_tamanho}`} />
                                )}
                                <Chip
                                    variant="outlined"
                                    label={`Preço: ${fmtBRL ? fmtBRL(selectedProduct.sell_price) : selectedProduct.sell_price}`}
                                />
                            </Stack>
                        </Box>

                        {submitState.status === "success" && <Alert severity="success">{submitState.message}</Alert>}
                        {submitState.status === "error" && <Alert severity="error">{submitState.message}</Alert>}
                        {submitState.status === "sending" && <Alert severity="info">Salvando…</Alert>}

                        <Grid container spacing={2}>
                            {/* Retroativo + Número (topo, antes da data) */}
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    {!isEdit && (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={!!form.number_manual}
                                                    onChange={(e) => {
                                                        const v = e.target.checked;
                                                        setForm((p) => ({
                                                            ...p,
                                                            number_manual: v,
                                                            number_ref_manual: v ? (p.number_ref_manual || "") : "",
                                                        }));
                                                    }}
                                                    color="warning"
                                                />
                                            }
                                            label="Número avulso (retroativo)"
                                            sx={{
                                                m: 0,
                                                "& .MuiFormControlLabel-label": { fontWeight: 800 },
                                            }}
                                        />
                                    )}

                                    {/* CREATE: só mostra o campo se retroativo estiver ligado */}
                                    {!isEdit && form.number_manual && (
                                        <TextField
                                            label="Número (avulso)"
                                            value={form.number_ref_manual}
                                            onChange={(e) => setForm((p) => ({ ...p, number_ref_manual: e.target.value }))}
                                            helperText="Digite o número retroativo (ex: 611)."
                                            sx={{ width: { xs: "100%", sm: 300 } }}
                                        />
                                    )}

                                    {/* EDIT: sempre mostra o número, read-only */}
                                    {isEdit && (
                                        <TextField
                                            label="Número"
                                            value={form.number_ref || "-"}
                                            InputProps={{ readOnly: true }}
                                            helperText="Gerado no backend."
                                            sx={{ width: { xs: "100%", sm: 300 } }}
                                        />
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Data de recebimento"
                                    value={form.received_at}
                                    onChange={(e) => setForm((p) => ({ ...p, received_at: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Status"
                                    value={form.status}
                                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                                >
                                    <MenuItem value="OPEN">Aberta</MenuItem>
                                    <MenuItem value="PENDING">Pendente</MenuItem>
                                    <MenuItem value="ATFACTORY">Na Fábrica</MenuItem>
                                    <MenuItem value="SENDTOFACTORY">Enviar para Fábrica</MenuItem>
                                    <MenuItem value="RETURNSELLER">Devolver para Revendedora</MenuItem>
                                    <MenuItem value="SOLVED">Resolvido</MenuItem>
                                    <MenuItem value="CANCELED">Anulado</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    disabled
                                    label="Código"
                                    value={form.product_code}
                                    onChange={(e) => setForm((p) => ({ ...p, product_code: e.target.value }))}
                                    placeholder="Ex: 301634 (ou -)"
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Mês de venda (auto)"
                                    value={saleMonthLabel}
                                    InputProps={{ readOnly: true }}
                                    helperText="Derivado de kit__pay_date"
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Lote"
                                    value={form.lot_code}
                                    onChange={(e) => setForm((p) => ({ ...p, lot_code: e.target.value }))}
                                    placeholder="Ex: LOTEJAN24 (pode preencher depois)"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Descrição do produto"
                                    value={form.product_description}
                                    onChange={(e) => setForm((p) => ({ ...p, product_description: e.target.value }))}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Problema apresentado"
                                    value={form.problem_reported}
                                    onChange={(e) => setForm((p) => ({ ...p, problem_reported: e.target.value }))}
                                    multiline
                                    minRows={3}
                                    placeholder="Obrigatório"
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    select
                                    label="É garantia?"
                                    value={form.is_warranty ? "yes" : "no"}
                                    onChange={(e) => setForm((p) => ({ ...p, is_warranty: e.target.value === "yes" }))}
                                >
                                    <MenuItem value="yes">SIM</MenuItem>
                                    <MenuItem value="no">NÃO</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Conserto interno"
                                    value={form.internal_repair ? "yes" : "no"}
                                    onChange={(e) => setForm((p) => ({ ...p, internal_repair: e.target.value === "yes" }))}
                                >
                                    <MenuItem value="yes">SIM</MenuItem>
                                    <MenuItem value="no">NÃO</MenuItem>
                                </TextField>
                            </Grid>


                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Anotações internas (opcional)"
                                    value={form.notes}
                                    onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                                    multiline
                                    minRows={2}
                                />
                            </Grid>

                            {/* ✅ SOLUÇÃO = observacao */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Solução"
                                    value={form.observacao}
                                    onChange={(e) => setForm((p) => ({ ...p, observacao: e.target.value }))}
                                    multiline
                                    minRows={2}
                                    placeholder="Descreva a solução aplicada / conclusão do caso"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <WarrantyPhotoUploader
                                    warrantyId={warrantyId}
                                    disabled={submitState.status === "sending"}
                                    existingUrlsCount={normalizePhotos(form.photos_urls_raw).length}
                                    onUploadedUrls={(items) => {
                                        // items pode ser string[] ou [{url, path}]
                                        const urlsNovas = (items || [])
                                            .map((x) => (typeof x === "string" ? x : x?.url))
                                            .filter((u) => typeof u === "string" && u.startsWith("http"));

                                        setForm((p) => {
                                            const atual = normalizePhotos(p.photos_urls_raw);
                                            const merged = [...atual, ...urlsNovas];
                                            return { ...p, photos_urls_raw: merged.join("\n") };
                                        });
                                    }}

                                />
                                <Box sx={{ mt: 2 }}>
                                    <WarrantyPhotosGallery
                                        photosRaw={form.photos_urls_raw}
                                        onRemoveUrl={(url) => {
                                            setForm((p) => {
                                                const next = normalizePhotos(p.photos_urls_raw).filter((u) => u !== url);
                                                return { ...p, photos_urls_raw: next.join("\n") };
                                            });
                                        }}
                                    />

                                </Box>


                            </Grid>

                        </Grid>

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Button variant="contained" color="success" onClick={handleSubmit} disabled={!canSubmit}>
                                {submitState.status === "sending" ? "Salvando..." : isEdit ? "Salvar alterações" : "Criar garantia"}
                            </Button>

                            <Button variant="outlined" color="warning" onClick={handleReset}>
                                {isEdit ? "Desfazer alterações" : "Limpar"}
                            </Button>

                            {/* <Button
                                    variant="outlined"
                                    onClick={() => {
                                        console.log("WARRANTY PAYLOAD PREVIEW:", payloadPreview);
                                        setSubmitState({ status: "success", message: "Preview do payload impresso no console." });
                                    }}
                                >
                                    Preview payload
                                </Button> */}
                        </Stack>
                    </Stack>
                </Paper>
            )}
        </>
    );
}
